  // Caminho relativo: src/app/services/data.service.ts
  import { HttpClient } from '@angular/common/http';
  import { Injectable } from '@angular/core';
  import { BehaviorSubject, from, Observable, of } from 'rxjs';
  import { v4 as uuidv4 } from 'uuid';
  import { map, switchMap, tap } from 'rxjs/operators';
  import { environment } from '../../environments/environment';
  import { ChecklistModel } from '../domains/models/checklist.model';
  import { FarmModel } from '../domains/models/farm.model';
  import { QuestionModel } from '../domains/models/question.model';
  import { SectionModel } from '../domains/models/section.model';
  import { ServiceOrderModel } from '../domains/models/service-order.model';
  import { UserModel } from '../domains/models/user.model';
  import { ValidatedUser } from '../domains/models/validate-user.model';
  import {
    generateTempCheckListNumber,
    generateTempSoNumber,
    itemExists,
    sortByProperty,
  } from '../utils';
  import { CACHE_OBJECTS, CacheService } from './cache.service';
  import { NetworkService } from './network.service';
  import { CommentModel } from '../domains/models/comment.model';
  import { VisitDiaryModel } from '../domains/models/visit-diary.model';
  import { PlanModel } from '../domains/models/plan.model';
  import { ProductReportModel } from '../domains/models/product-report.model';
  import { LaboratoryModel } from '../domains/models/laboratory.mode';
  import { ProblemsCasesModel } from '../domains/models/problems-cases.model';
  import { ProblemsTypeModel } from '../domains/models/problems-types.model';

  @Injectable({
    providedIn: 'root',
  })
  export class DataService {
    _httpClient: HttpClient;
    imageBase64: any[];
    selectedFarm: any;
    problemscases: ProblemsCasesModel[] = [];
    problemsTypesSubject = new BehaviorSubject<ProblemsTypeModel[]>([]);
    problemscasesSubject = new BehaviorSubject<ProblemsCasesModel[]>([]);
    problemscases$ = this.problemscasesSubject.asObservable();
    problemsTypes$ = this.problemsTypesSubject.asObservable();
    private problemsTypes: ProblemsTypeModel[] = [];


    constructor(
      private http: HttpClient,
      private cacheService: CacheService,
      private networkService: NetworkService
    ) {
      this._httpClient = http;
    }

    /**
     *
     * @param farm Guardar a fazenda selecionada
     */
    setSelectedFarm(farm: any): void {
      this.selectedFarm = farm;
    }

    /**
     *
     * @returns Pegar a fazenda selecionada
     */
    getSelectedFarm(): any {
      return this.selectedFarm;
    }

    /**
     * DO LOGIN
     * Condition: Online
     */
    async doLogin(userModel: UserModel): Promise<ValidatedUser> {
      try {
        const credentials = {
          uid: userModel.email,
          password: userModel.password,
        };

        const validatedUser = await this.http
          .post<ValidatedUser>(`${environment.authApi}/auth/login`, credentials)
          .pipe(map((result) => result))
          .toPromise();

        await this.cacheService.insert('logged-user', validatedUser);

        return validatedUser;
      } catch (error) {
        console.error('Erro ao efetuar login', error);
        throw error;
      }
    }

    /**
     * Faz download dos itens
     * necessário quando online
     */
    async syncContentWhenOnline(userId: number) {
      try {
        if (environment.offlineStore.farms) {
          try {
            await this.getUserFarmsOnline();
          } catch (error) {
            console.error('[DEBUG] Erro em getUserFarmsOnline:', error);
          }
        }

        if (environment.offlineStore.compatibility) {
          try {
            await this.createCompatibilityCache();
          } catch (error) {
            console.error('[DEBUG] Erro em createCompatibilityCache:', error);
          }
        }

        if (environment.offlineStore.reasonList) {
          try {
            await this.createCacheReasons();
          } catch (error) {
            console.error('[DEBUG] Erro em createCacheReasons:', error);
          }
        }

        if (environment.offlineStore.problems) {
          try {
            await this.createCacheProblems();
          } catch (error) {
            console.error('[DEBUG] Erro em createCacheProblems:', error);
          }
        }

        if (environment.offlineStore.productList) {
          try {
            await this.getProductList();
          } catch (error) {
            console.error('[DEBUG] Erro em getProductList:', error);
          }
        }

        if (environment.offlineStore.sections) {
          try {
            await this.getAllSectionsOnline();
          } catch (error) {
            console.error('[DEBUG] Erro em getAllSectionsOnline:', error);
          }
        }

        if (environment.offlineStore.questions) {
          try {
            await this.getAllQuestionsOnline();
          } catch (error) {
            console.error('[DEBUG] Erro em getAllQuestionsOnline:', error);
          }
        }

        if (environment.offlineStore.userProfile) {
          try {
            await this.getUserProfileOnline(userId);
          } catch (error) {
            console.error('[DEBUG] Erro em getUserProfileOnline:', error);
          }
        }

        if (environment.offlineStore.serviceOrders) {
          try {
            await this.syncServiceOrders();
          } catch (error) {
            console.error('[DEBUG] Erro em syncServiceOrders:', error);
          }
        }

        if (environment.offlineStore.checklists) {
          try {
            await this.syncChecklists();
          } catch (error) {
            console.error('[DEBUG] Erro em syncChecklists:', error);
          }
        }

        if (environment.offlineStore.visitDiaries) {
          try {
            await this.syncVisitDiaries();
          } catch (error) {
            console.error('[DEBUG] Erro em syncVisitDiaries:', error);
          }
        }

        if (environment.offlineStore.managementPlans) {
          try {
            await this.syncAllPlans();
          } catch (error) {
            console.error('[DEBUG] Erro em syncAllPlans:', error);
          }
        }

        if (environment.offlineStore.visitDiaries) {
          try {
            await this.sendCreatedVisitsWhenOnline();
          } catch (error) {
            console.error('[DEBUG] Erro em sendCreatedVisitsWhenOnline:', error);
          }
        }
      } catch (error) {
        console.error('[DEBUG] Erro geral em syncContentWhenOnline:', error);
      }
    }


    /**
     * GET USER PROFILE
     *
     * Condition: Online
     */
    async getUserProfileOnline(userId: number): Promise<UserModel> {
      try {
        const profile = await this.http
          .get<UserModel>(`${environment.authApi}/api/v1/user/${userId}`)
          .pipe(map((profile) => profile))
          .toPromise();


        if (environment.offlineStore.userProfile) {
          const userProfile = await this.cacheService.insert('user-profile', profile);
          return userProfile;
        } else {
          return profile;
        }
      } catch (error) {
        console.error('Erro ao recuperar perfil do usuário do servidor', error);
      }
    }

    /**
     *
     * @param userId Procesamento de sincronização em segundo plano
     * @param progressCallback
     */
    // async syncContentWhenOnlineBackground(userId: number, progressCallback?: (progress: number) => void) {
    //   const steps = 12; // Número total de etapas
    //   let currentStep = 0;

    //   const updateProgress = () => {
    //     currentStep++;
    //     const progress = Math.min(Math.round((currentStep / steps) * 100), 100); // Limita o progresso a no máximo 100%
    //     console.log(`Etapa ${currentStep}/${steps} concluída. Progresso: ${progress}%`);
    //     if (progressCallback) {
    //       progressCallback(progress); // Atualiza o progresso via callback
    //     }
    //   };

    //   await this.getUserFarmsOnline();
    //   updateProgress();

    //   await this.createCompatibilityCache();
    //   updateProgress();

    //   await this.createCacheReasons();
    //   updateProgress();

    //   await this.createCacheProblems();
    //   updateProgress();

    //   await this.getProductList();
    //   updateProgress();

    //   await this.getAllSectionsOnline();
    //   updateProgress();

    //   await this.getAllQuestionsOnline();
    //   updateProgress();

    //   await this.getUserProfileOnline(userId);
    //   updateProgress();

    //   await this.syncServiceOrders();
    //   updateProgress();

    //   await this.syncChecklists();
    //   updateProgress();

    //   await this.syncVisitDiaries();
    //   updateProgress();

    //   await this.syncAllPlans();
    //   updateProgress();
    // }

    async getUserProfile(): Promise<UserModel> {
      try {
        const profile = await this.cacheService.get('user-profile');
        return profile;
      } catch (error) {
        console.error('Erro ao recuperar perfil do usuário', error);
      }
    }

    /**
     * UPDATE USER PROFILE
     *
     * Condition: Online
     */
    updateUserProfileWhenOnline(user: UserModel): Observable<any> {
      try {
        const userId = user.id;
        delete user.id;
        const updatedUserProfile = this.http.put<any>(
          `${environment.authApi}/api/v1/user/${userId}`,
          user
        );
        return updatedUserProfile;
      } catch (error) {
        console.error('Erro ao atualizar o perfil do usuário online.', error);
      }
    }

    /**
     * UPDATE USER PROFILE
     *
     * Condition: Offline
     */
    async updateUserProfile(user: UserModel): Promise<any> {
      try {
        const updatedUserProfile = await this.cacheService.update(
          'user-profile',
          user
        );
        return updatedUserProfile;
      } catch (error) {
        console.error(
          'Erro ao atualizar perfil do usuário no dispositivo.',
          error
        );
      }
    }

    /**
     * UPDATE USER PHOTO
     *
     * Condition: Online
     *
     * Experimental: Verificar quando isso será útil
     * uma vez que o usuário sincroniza a foto no login
     *
     * Observação: Nào está sendo usado até o momento
     *
     * 01/10/2021
     */
    async updateUserPhotoOnline(id: number): Promise<UserModel> {
      try {
        const foundUserProfile = await this.cacheService.get('user-profile');
        const photo = foundUserProfile.photo;
        const userPhoto = await this.http
          .post<UserModel>(`${environment.authApi}/api/v1/user/${id}/photo`, {
            photo,
          })
          .toPromise();
        return userPhoto;
      } catch (error) {
        console.error('Erro ao atualizar a foto do usuário', error);
      }
    }

    /**
     * Condition: Offline
     *
     * Sincroniza quando user estiver online
     */
    async updateUserPhoto(photo: string): Promise<UserModel> {
      try {
        const foundUserProfile = await this.cacheService.get('user-profile');
        foundUserProfile.photo = photo;

        const updatedUserProfile = await this.cacheService.update(
          'user-profile',
          foundUserProfile
        );
        return updatedUserProfile.photo;
      } catch (error) {
        console.error('Erro ao atualizar a foto do usuário', error);
      }
    }

    /**
     * Condition: Online
     */
    updateUserPassword(user: any): Observable<any> {
      return this.http.post<any>(`${environment.authApi}/auth/changepass`, user);
    }

    /**
     * GET FARMS
     * Condition: Online
     *
     * Must be on line at least
     * at once to get farms by logged user
     */
    async getUserFarmsOnline(): Promise<FarmModel[]> {
      try {
        const params = `?orderby=farm_name&order=asc`;
        const userFarms = await this.http
          .get<FarmModel[]>(`${environment.api}/api/v1/farm/${params}`)
          .pipe(map((farms) => farms))
          .toPromise();


        if (environment.offlineStore.farms) {
          const storedFarms = await this.cacheService.insert('get-user-farms', userFarms);
          return storedFarms;
        } else {
          return userFarms;
        }
      } catch (error) {
        console.error('Erro ao buscar lista de farms do backend', error);
      }
    }

    /**
     *
     * Condition: Offline
     */
    async getUserFarms(): Promise<FarmModel[]> {
      try {
        const userFarms = await this.cacheService.get('get-user-farms');
        return userFarms;
      } catch (error) {
        console.error('Erro ao buscar lista de farms do backend', error);
      }
    }

    /**
     * GET FARMS By USERID
     *
     * Condition: Offline
     */
    async getFarmById(id: number): Promise<FarmModel> {
      try {
        const farms = await this.cacheService.get('get-user-farms');
        const farmId = farms.filter((farm: FarmModel) => farm.id == id);

        return farmId[0];
      } catch (error) {
        console.error('Erro ao buscar fazenda por id', error);
      }
    }

    /**
     * VISIT DIARIES
     *
     * Condition: Online & Offline
     */

    async createVisitDiaryWhenOnline(visitDiary: any): Promise<VisitDiaryModel> {
      try {
        const response = await this.http
          .post<VisitDiaryModel[]>(`${environment.api}/api/v1/diary`, visitDiary)
          .pipe(map((visitDiary) => visitDiary))
          .toPromise();

        return response[0];
      } catch (error) {
        console.error('Erro ao criar o diário de visitas', error);
      }
    }

    /**
     * Condition: Offline
     */
    async createVisitDiaryWhenOffline(visitDiary: any): Promise<VisitDiaryModel> {
      try {
        const response = await this.cacheService.get('visit-diaries');
        response.push(visitDiary);

        const responseValue = await this.cacheService.insert(
          'visit-diaries',
          response
        );
        return responseValue;
      } catch (error) {
        console.error('Erro ao criar ordem de serviço', error);
      }
    }

    async createStockWhenOffline(stockPayload: any): Promise<any> {
      try {
        let offlineStocks = await this.cacheService.get('offline-stocks') || [];
        offlineStocks.push(stockPayload);
        return await this.cacheService.insert('offline-stocks', offlineStocks);
      } catch (error) {
        console.error("Erro ao armazenar estoque offline", error);
      }
    }

    async getAllReasons(): Promise<any[]> {
      try {
        const reasonList = await this.http
          .get<any[]>(`${environment.api}/api/v1/reason_list`)
          .pipe(map((list) => list))
          .toPromise();

        return Array.isArray(reasonList) ? reasonList : [];
      } catch (error) {
        console.error('Erro ao carregar a lista de motivos', error);
        return [];
      }
    }

    async getAllProblems() {
      try {
        const problemsList = await this.http
          .get<any[]>(`${environment.api}/api/v1/problem_list?orderby=display&order=asc`)
          .toPromise();

        await this.cacheService.insert('problem-list', problemsList);
        return problemsList;
      } catch (error) {
        console.error('Erro ao carregar a lista de problemas', error);
        return [];
      }
    }


    async getResolutionSubId(id) {
      try {
        const resolution = await this.http
          .get<any[]>(`${environment.api}/api/v1/resolution_os/sub/${id}`)
          .toPromise();
        return resolution;
      } catch (error) {
      }
    }

    async getResolutionId(id) {
      try {
        const resolution = await this.http
          .get(`${environment.api}/api/v1/resolution_os/${id}`)
          .toPromise();
        return resolution;
      } catch (error) {
      }
    }


    async getAllVisitsWhenOnline(farmId?: number, date?: string, daysBack: number = 30): Promise<any[]> {
        try {
            const today = new Date();
            const pastDate = new Date();
            pastDate.setDate(today.getDate() - daysBack);

            const startDate = date ? this.formatDate(date) : this.formatDate(pastDate);
            const endDate = date ? this.formatDate(date) : this.formatDate(today);


            const visits = await this.http.get<any>(
                `${environment.api}/api/v1/diary?farm_id=${farmId}&start_date=${startDate}&end_date=${endDate}`
            ).pipe(map(visitDiaries => visitDiaries)).toPromise();

            return visits?.rows || [];
        } catch (error) {
            console.error('❌ [API] Erro ao buscar visitas:', error);
            return [];
        }
    }

    private formatDate(dateInput: string | Date): string {
      const date = new Date(dateInput);

      if (isNaN(date.getTime())) {
          console.error('⚠️ Data inválida ao formatar:', dateInput);
          return '';
      }

      return date.toISOString().split('T')[0];
    }

    async getVisitsWhenOffLine(farmId?: number): Promise<VisitDiaryModel[]> {
      try {
        let visitDiaries = await this.cacheService.get('visit-diaries');
        visitDiaries = visitDiaries.filter(
          (visit: VisitDiaryModel) => visit.farm_id == farmId
        );

        visitDiaries.sort(sortByProperty('created_at'));

        //console.log('visitas', visitDiaries);

        return visitDiaries;
      } catch (error) {
        console.error('Erro ao carregar ordens de serviço', error);
      }
    }

    async sendCreatedVisitsWhenOnline(): Promise<VisitDiaryModel[]> {
      let visitDiaries: VisitDiaryModel[] = [];
      try {
        let cachedVisitDiaries = await this.cacheService.get('visit-diaries');

        const offlineVisits = cachedVisitDiaries.filter(
          (visit: VisitDiaryModel) => visit && !visit.id
        );


        if (offlineVisits && offlineVisits.length > 0) {

          await this.http
            .post(`${environment.api}/api/v1/diary_array`, offlineVisits)
            .toPromise();

          const responseGet: any = await this.http
            .get(`${environment.api}/api/v1/diary`)
            .toPromise();

          if (responseGet && responseGet.rows) {
            visitDiaries = responseGet.rows.map((visit: VisitDiaryModel) => {
              visit.transmitted = true;
              return visit;
            });
          }

          await this.cacheService.update('visit-diaries', []);
        } else {
        }
        return visitDiaries;
      } catch (error) {
        return [];
      }
    }

    async sendCreatedStocksWhenOnline(): Promise<any> {
      try {
        let offlineStocks = await this.cacheService.get('offline-stocks') || [];
        if (offlineStocks.length > 0) {
          // Enviar os estoques offline para o servidor (pode ser em lote ou individualmente)
          await this.http.post(`${environment.api}/api/v1/product_stock_batch`, offlineStocks).toPromise();
          // Após o envio, limpa o cache dos estoques offline
          await this.cacheService.update('offline-stocks', []);
        }
      } catch (error) {
        console.error("Erro ao sincronizar estoques offline", error);
      }
    }


    async getVisitByIdWhenOnline(id) {
      try {
        const visitById = await this.http
          .get(`${environment.api}/api/v1/diary/${id}`)
          .toPromise();
        return visitById;
      } catch (error) {
      }
    }

    async getVisitByIdWhenOffline(id: string): Promise<VisitDiaryModel> {
      try {
        const foundVisitDiary = await this.cacheService.get('visit-diaries');
        const visitById = foundVisitDiary.find(
          (visit: VisitDiaryModel) => visit._id == id
        );
        return visitById;
      } catch (error) {
      }
    }

    /**
     * SERVICE ORDERS
     *
     * Condition: Online
     */
    async getAllServiceOrdersWhenOnline(farmId?: number): Promise<any[]> {
      await this.sendCreatedVisitsWhenOnline();

      try {
        const serviceOrders = await this.http
          .get<any>(`${environment.api}/api/v1/service_order?farm_id=${farmId}`)
          .pipe(map((serviceOrders) => serviceOrders))
          .toPromise();

        const { rows } = serviceOrders;

        if (rows && rows.length) {
          let cachedServiceOrders: ServiceOrderModel[] = (await this.cacheService.get('service-orders') || [])
            .filter(order => order && order.id); // filtrando itens inválidos

          rows.forEach((serviceOrder: ServiceOrderModel) => {
            serviceOrder.transmitted = true;
            if (!itemExists(serviceOrder, cachedServiceOrders)) {
              cachedServiceOrders.push(serviceOrder);
            } else {
              cachedServiceOrders = cachedServiceOrders.map(
                (cachedOrder: ServiceOrderModel) =>
                  cachedOrder.id === serviceOrder.id ? serviceOrder : cachedOrder
              );
            }
          });

          await this.cacheService.update('service-orders', cachedServiceOrders);
          rows.sort(sortByProperty('created_at'));
          return rows;
        } else {
          return [];
        }
      } catch (error) {
        console.error('Erro ao carregar todas as ordens de serviço online', error);
        return [];
      }
    }



    /**
     * SERVICE ORDERS
     *
     * * Condition: Offline
     */
    async getServiceOrders(farmId?: number): Promise<ServiceOrderModel[]> {
      try {
        let serviceOrders: ServiceOrderModel[] = (await this.cacheService.get('service-orders')) || [];

        serviceOrders = serviceOrders.filter((so: ServiceOrderModel) => so && so.farm_id === farmId);

        serviceOrders = serviceOrders.map((os: ServiceOrderModel, index: number) => {
          if (!os.SO_number) {
            os.SO_number = generateTempSoNumber(index);
          }
          return os;
        });

        serviceOrders.sort(sortByProperty('created_at'));
        return serviceOrders;
      } catch (error) {
        console.error('Erro ao carregar ordens de serviço', error);
        return [];
      }
    }


    /**
     * Condition: Offline
     */
    async getServiceOrderById(id: string): Promise<ServiceOrderModel> {
      try {
        let comments = await this.cacheService.get(
          CACHE_OBJECTS.SERVICE_ORDERS_COMMENTS
        );
        comments = comments.filter(
          (comment: CommentModel) => comment._serviceorder_id === id
        );
        const foundServiceOrders = await this.cacheService.get('service-orders');
        const serviceOrderById = foundServiceOrders.find(
          (so: ServiceOrderModel) => so._id === id
        );
        if (!serviceOrderById.comments) {serviceOrderById.comments = [];}
        serviceOrderById.comments = serviceOrderById.comments.concat(comments);
        return serviceOrderById;
      } catch (error) {
      }
    }

    async getServiceOrderByIdWhenOnline(id) {
      const foundServiceOrder = await this.http
        .get<any>(`${environment.api}/api/v1/service_order/${id}`)
        .toPromise();

      if (environment.offlineStore.serviceOrders) {
        await this.cacheService.insert(`service-order-${id}`, foundServiceOrder);
      } else {
      }

      return foundServiceOrder;
    }

    /**
     * Condition: Offline
     */
    async updateServiceOrder(
      serviceOrder: ServiceOrderModel
    ): Promise<ServiceOrderModel> {
      try {
        if (serviceOrder._id) {
          const updatedServiceOrder = await this.updateServiceOrderOnline(
            serviceOrder
          );
          if (updatedServiceOrder) {
            serviceOrder = updatedServiceOrder;
          }
        } else {
          await this.createServiceOrder(serviceOrder);
        }

        const foundServiceOrders = await this.cacheService.get('service-orders');

        const indexOf = foundServiceOrders.findIndex(
          (foundSO: ServiceOrderModel) => serviceOrder._id == foundSO._id
        );
        foundServiceOrders[indexOf] = serviceOrder;

        await this.cacheService.update('service-orders', foundServiceOrders);
        return foundServiceOrders[indexOf];
      } catch (error) {
        console.error('Erro ao atualizar ordem de serviço', error);
      }
    }

    async updateServiceOrderOnline(
      serviceOrder: ServiceOrderModel
    ): Promise<ServiceOrderModel> {
      try {
        const updatedServiceOrder = await this.http
          .put<ServiceOrderModel[]>(
            `${environment.api}/api/v1/service_order/${serviceOrder.id}`,
            serviceOrder
          )
          .pipe(map((serviceOrders) => serviceOrders))
          .toPromise();

        return updatedServiceOrder[0];
      } catch (error) {
        console.error('Erro ao atualizar ordem de serviço', error);
      }
    }

    /**
     * Condition: Offline
     */
    async createServiceOrder(body: any): Promise<ServiceOrderModel> {
      try {
        const foundServiceOrders = await this.cacheService.get('service-orders');

        const tempOS = {
          ...body,
          id: null,
          _id: uuidv4(),
          is_edited: true
        };

        foundServiceOrders.push(tempOS);

        return await this.cacheService.insert('service-orders', foundServiceOrders);
      } catch (error) {
        console.error('Erro ao criar ordem de serviço', error);
      }
    }


    /**
     * Condition: Online
     */
    async createServiceOrderOnline(body: any): Promise<ServiceOrderModel> {
      try {
        const createdServiceOrder = await this.http
          .post<ServiceOrderModel[]>(
            `${environment.api}/api/v1/service_order`,
            body
          )
          .pipe(map((serviceOrders) => serviceOrders))
          .toPromise();

        return createdServiceOrder[0];
      } catch (error) {
        console.error('Erro ao criar ordem de serviço', error);
      }
    }

    async sendComment(comment: CommentModel): Promise<CommentModel> {
      return new Promise(async (resolve, reject) => {
        if (
          (await this.networkService.isConnected()) &&
          comment.serviceorder_id
        ) {
          try {
            const response = await this.http
              .post(
                `${environment.authApi}/api/v1/service_order/${comment.serviceorder_id}/comment`,
                comment
              )
              .toPromise();
            resolve(response as CommentModel);
          } catch (error) {
            reject(error);
          }
        } else {
          let foundComments = await this.cacheService.get(
            CACHE_OBJECTS.SERVICE_ORDERS_COMMENTS
          );
          if (!foundComments) {foundComments = [];}
          foundComments.push(comment);
          await this.cacheService.insert(
            CACHE_OBJECTS.SERVICE_ORDERS_COMMENTS,
            foundComments
          );
          resolve(comment);
        }
      });
    }

    async sendCreatedServiceOrdersWhenOnline(): Promise<ServiceOrderModel[]> {
      const cachedServiceOrders: ServiceOrderModel[] = (await this.cacheService.get('service-orders')) || [];

      const offlineOS = cachedServiceOrders.filter(
        (os: ServiceOrderModel) => os && (!os.id || os.is_edited)
      );

      const sentOS: ServiceOrderModel[] = [];

      for (const os of offlineOS) {
        try {
          const updatedOS = await this.createServiceOrderOnline(os);
          sentOS.push(updatedOS);
        } catch (error) {
        }
      }

      const remainingOS = cachedServiceOrders.filter(
        (os: ServiceOrderModel) => os && os.id && !os.is_edited
      );

      const updatedCache = [...remainingOS, ...sentOS];
      await this.cacheService.update('service-orders', updatedCache);

      return sentOS;
    }


    /**
     * CHECKLISTS
     * Condition: Online
     */

    async sendCreatedChecklistsWhenOnline(farmId) {
      try {
        const results = [];
        let cachedChecklists = await this.cacheService.get('checklist-answers');
        cachedChecklists = cachedChecklists.filter(
          (createdChecklist) => !createdChecklist.id
        );

        for (const checklist of cachedChecklists) {
          const sentChecklist = await this.http
            .post<ChecklistModel>(
              `${environment.api}/api/v1/checklist`,
              checklist
            )
            .pipe(map((result) => result))
            .toPromise();

          if (sentChecklist) {
            let checklistArray: any = await this.http
              .get(`${environment.api}/api/v1/checklist/?farm_id=${farmId}`)
              .pipe(map((result) => result))
              .toPromise();
            checklistArray = checklistArray.map((checklist) => ({ ...checklist, transmitted: true }));
            const cachedChecklistArray = await this.cacheService.insert(
              'checklist-answers',
              checklistArray
            );
          }

          // if (sentChecklist) {
          //   let checklistArray = await this.http.get(`${environment.api}/api/v1/checklist/?farm_id=${farmId}`).pipe(map(result => result)).toPromise();
          //   results.push(checklistArray);
          //   console.warn('enviando checklist...', checklist);
          // }
        }

        // if (results.length) {
        //   // Atualiza o cache
        //   await this.cacheService.update('checklist-answers', results);
        // }
        //
        // return results;
      } catch (error) {
        console.error('Erro ao enviar checklists para o servidor', error);
      }
    }

    async getChecklistTypeWhenOnline() {
      const checkListTypes = await this.http
        .get(`${environment.api}/api/v1/section`)
        .toPromise();
      const cachedListTypes = await this.cacheService.insert(
        'all-sections',
        checkListTypes
      );
      return cachedListTypes;
    }

    async getChecklistTypeWhenOffline() {
      const cachedChecklists = await this.cacheService.get('all-sections');
      return cachedChecklists;
    }

    /**
     *
     * Condition: Online
     */
    async getAllChecklistsWhenOnline(farmId): Promise<ChecklistModel[]> {
      await this.sendCreatedChecklistsWhenOnline(farmId);

      let serverChecklists: ChecklistModel[] = await this.http
        .get<ChecklistModel[]>(
          `${environment.api}/api/v1/checklist/?farm_id=${farmId}`
        )
        .pipe(map((result) => result))
        .toPromise();
      serverChecklists = serverChecklists.length ? serverChecklists : [];

      if (serverChecklists.length) {
        const checklists: ChecklistModel[] = await this.cacheService.get(
          `checklist-answers`
        );

        serverChecklists.forEach((serverChecklist) => {
          if (!itemExists(serverChecklist, checklists)) {
            checklists.push(serverChecklist);
            this.cacheService.update(`checklist-answers`, checklists);
          }
        });

        serverChecklists.sort(sortByProperty('created_at'));
      }

      return serverChecklists;
    }

    async syncVisitDiaries() {
      let visitDiaryArray = [];
      const userFarms = await this.cacheService.get('get-user-farms');
      for (const farm of userFarms) {
        try {
          let visitDiaries: any = await this.http
            .get(`${environment.api}/api/v1/diary?farm_id=${farm.id}`)
            .toPromise();
          visitDiaries = visitDiaries.rows;
          visitDiaries.map((visitDiary) => {
            visitDiary.transmitted = true;
          });
          if (visitDiaries?.length) {
            visitDiaryArray = visitDiaryArray.concat(visitDiaries);
          }
        } catch (e) {
        }
      }
      if (environment.offlineStore.visitDiaries) {
        return await this.cacheService.insert('visit-diaries', visitDiaryArray);
      } else {
        return visitDiaryArray;
      }
    }

    async syncChecklists() {
      let checklistArray = [];
      const userFarms = await this.cacheService.get('get-user-farms');
      for (const farm of userFarms) {
        try {
          const checklists: any = await this.http
            .get(`${environment.api}/api/v1/checklist/?farm_id=${farm.id}`)
            .toPromise();
          checklists.map((checklist) => {
            checklist.transmitted = true;
          });
          if (checklists?.length) {
            checklistArray = checklistArray.concat(checklists);
          }
        } catch (e) {
          console.log('Erro ao sincronizar os dados de checklists', e);
        }
      }
      if (environment.offlineStore.checklists) {
        return await this.cacheService.insert('checklist-answers', checklistArray);
      } else {
        return checklistArray;
      }
    }

    async syncServiceOrders() {
      let serviceOrderArray = [];
      const userFarms = await this.cacheService.get('get-user-farms');

      await this.sendCreatedServiceOrdersWhenOnline();

      for (const farm of userFarms) {
        try {
          let response: any = await this.http
            .get(`${environment.api}/api/v1/service_order/?farm_id=${farm.id}`)
            .toPromise();
          let serviceOrders = response.rows;
          serviceOrders.forEach((serviceOrder) => {
            serviceOrder.transmitted = true;
          });
          if (serviceOrders && serviceOrders.length) {
            serviceOrderArray = serviceOrderArray.concat(serviceOrders);
          }
        } catch (e) {
        }
      }


      let cachedServiceOrders = await this.cacheService.get('service-orders');

      const offlineOrders = cachedServiceOrders.filter(order => !order.id || order.is_edited);

      const mergedOrders = [
        ...offlineOrders,
        ...serviceOrderArray.filter(serverOrder =>
          !offlineOrders.some(offline => offline._id === serverOrder._id)
        )
      ];

      await this.cacheService.insert('service-orders', mergedOrders);
      return mergedOrders;
    }

    async createCacheReasons() {
      if (environment.offlineStore.reasonList) {
        if (!window.localStorage.getItem('reason-list')) {
          const reasonList = await this.getAllReasons();
          await this.cacheService.insert('reason-list', reasonList);
          window.localStorage.setItem('reason-list', 'true');
        }
      }
    }

      async createCacheProblems() {
        if (environment.offlineStore.problems) {
          if (!window.localStorage.getItem('problem-list')) {
            const problemsList = await this.getAllProblems();
            await this.cacheService.insert('problem-list', problemsList);
            window.localStorage.setItem('problem-list', 'true');
          }
        }
      }


    async syncProblems() {
      await this.cacheService.insert('problem-list', this.getAllProblems());
    }

    async createCompatibilityCache() {
      if (environment.offlineStore.compatibility) {
        this.cacheService.insert(
          'compatibility-table',
          this.getCompatibilityTable()
        );
      }
    }

    /**
     *
     * Condition: Offline
     */
    async getChecklists(farmId: number): Promise<ChecklistModel[]> {
      try {
        let checklists = await this.cacheService.get('checklist-answers');
        checklists = checklists.filter(
          (checklist: ChecklistModel) => checklist.farm_id == farmId
        );

        if (checklists.length) {
          checklists = checklists.map(
            (checklist: ChecklistModel, index: number) => ({
              _id: checklist._id,
              checklist_number: checklist.checklist_number
                ? checklist.checklist_number
                : generateTempCheckListNumber(index),
              created_at: checklist.created_at
                ? checklist.created_at
                : new Date(),
              time_at: checklist.created_at ? checklist.created_at : new Date(),
              transmitted: checklist.transmitted,
            })
          );

          checklists.sort(sortByProperty('checklist_number'));
        }

        return checklists;
      } catch (error) {
        console.error('Erro ao carregar checklists', error);
      }
    }

    /**
     * ANSWERS
     */
    async saveChecklistAnswers(questions: any) {
      const checklistAnswers: any[] = await this.cacheService.get(
        'checklist-answers'
      );

      let questionOnStorage = checklistAnswers.find(
        (a) => a._id == questions._id
      );
      if (questionOnStorage) {
        questionOnStorage = questions;
        await this.cacheService.insert('checklist-answers', checklistAnswers);
        return checklistAnswers;
      }

      checklistAnswers.push(questions);
      await this.cacheService.insert('checklist-answers', checklistAnswers);
      // console.log(
      //   'lista de checklists',
      //   this.cacheService.get('checklist-answers')
      // );

      return checklistAnswers;
    }

    async saveChecklistAnswersWhenOnline(questions: any) {
      try {
        const sentChecklist = await this.http
          .post<ChecklistModel>(`${environment.api}/api/v1/checklist`, questions)
          .pipe(map((result) => result))
          .toPromise();
        const cachedChecklists = await this.cacheService.get('checklist-answers');
        cachedChecklists.push(sentChecklist);
        await this.cacheService.insert('checklist-answers', cachedChecklists);
        return sentChecklist;
      } catch (error) {
        console.error('Erro ao enviar checklist para o servidor', error);
      }
    }

    /**
     * Condition: Online
     */
    async getChecklistById(id: number): Promise<ChecklistModel> {
      try {
        const checkLists = await this.cacheService.get('checklist-answers');
        const checklistById = checkLists.find(
          (checklist: ChecklistModel) => checklist._id == id
        );
        return checklistById;
      } catch (error) {
        console.error('Erro ao carregar checklist', error);
      }
    }

    async verifyServiceOrdersTransmitted(): Promise<boolean> {
      const serviceOrders = await this.cacheService.get('service-orders');
      const transmitted = serviceOrders.every(
        (transm: ServiceOrderModel) => transm.transmitted == true
      );
      return transmitted;
    }

    async verifyChecklistsTransmitted(): Promise<boolean> {
      const checklists = await this.cacheService.get('checklist-answers');
      const transmitted = checklists.every(
        (transm: ChecklistModel) => transm.transmitted == true
      );
      return transmitted;
    }

    /**
     * QUESTIONS
     * Condition: Online
     */
    async getAllQuestionsOnline(): Promise<QuestionModel[]> {
      try {
        const questions = await this.http
          .get<QuestionModel[]>(`${environment.api}/api/v1/question?active=true`)
          .pipe(map((question) => question))
          .toPromise();

        if (environment.offlineStore.questions) {
          await this.cacheService.insert('all-questions', questions);
          return await this.cacheService.get('all-questions');
        } else {
          return questions;
        }
      } catch (error) {
        console.error('Erro ao recuperar lista de perguntas do Checklist', error);
      }
    }
    /**
     *
     * Condition: Offline
     */
    async getAllQuestions(): Promise<QuestionModel[]> {
      try {
        const allQuestions = await this.cacheService.get('all-questions');
        return allQuestions;
      } catch (error) {
        console.error('Erro ao recuperar lista de perguntas do Checklist', error);
      }
    }

    /**
     * SECTIONS
     * Condition: Online
     */
    async getAllSectionsOnline(): Promise<SectionModel[]> {
      try {
        const sections = await this.http
          .get<SectionModel[]>(`${environment.api}/api/v1/section/`)
          .pipe(map((section) => section))
          .toPromise();

        if (environment.offlineStore.sections) {
          const allSections = await this.cacheService.insert('all-sections', sections);
          return allSections;
        } else {
          return sections;
        }
      } catch (error) {
        console.error('Erro ao carregar as sections', error);
      }
    }

    /**
     * SECTIONS
     * Condition: Offline
     */
    async getAllSectionsWhenOnline() {
      try {
        let allSections: any = await this.http
          .get(`${environment.api}/api/v1/section`)
          .toPromise();
        allSections = allSections.filter(
          (sectionsActive) => sectionsActive.active
        );
        await this.cacheService.insert('all-sections', allSections);
        const allSectionsFiltered = await this.cacheService.get('all-sections');
        return allSectionsFiltered;
      } catch (error) {
        console.error('Erro ao carregar as sections', error);
      }
    }

    async getAllSectionsWhenOffline() {
      const allSections: any = await this.cacheService.get('all-sections');
      const allSectionsFiltered = allSections.filter(
        (sectionActive) => sectionActive.active
      );
      return allSectionsFiltered;
    }

    /**
     * PRODUCT REPORTS (Laudos)
     * Condition: ainda não sei
     */
    getProductReports(params: string) {
      const productReports = this.http.get<any>(
        `${environment.api}/api/v1/product_report${params}`
      );
      return productReports;
    }

    /**
     * Condition: ainda não sei
     */
    getProductReportById(id: number): Promise<ProductReportModel> {
      const productReportById = this.http
        .get<ProductReportModel>(`${environment.api}/api/v1/product_report/${id}`)
        .toPromise();

      return productReportById;
    }

    downloadPDF(url: string): any {
      return this.http.get(url, { responseType: 'blob' }).toPromise();
    }

    async getCompatibilityTable() {
      return await this.http
        .get(`${environment.api}/api/v1/compatibility/results`)
        .pipe(map((elem) => elem))
        .toPromise();
    }

    async getCulturesList() {
      const cultureList = await this.http
        .get(`${environment.api}/api/v1/culture`)
        .pipe(map((elem) => elem))
        .toPromise();
      await this.cacheService.insert('cultureList', cultureList);
      return cultureList;
    }

    async createManagementPlan(managementPlan) {
      const plan = await this.http
        .post(`${environment.api}/api/v1/plan`, managementPlan)
        .pipe(map((elem) => elem))
        .toPromise();
      await this.cacheService.insert('last-management-plan', plan);
      return plan;
    }

    async getPlanById(planId) {
      const plan = await this.http
        .get<PlanModel>(`${environment.api}/api/v1/plan/${planId}`)
        .toPromise();
      return plan;
    }

    async syncAllPlans() {
      const allPlans: any = await this.http
        .get(`${environment.api}/api/v1/plan`)
        .toPromise();

      if (environment.offlineStore.managementPlans) {
        await this.cacheService.insert('management-plans', allPlans.plans);
      }
    }

    async getProductList() {
      const productList: any = await this.http
        .get(`${environment.api}/api/v1/product`)
        .toPromise();

      if (environment.offlineStore.productList) {
        await this.cacheService.insert('product-list', productList.products);
      }

      return productList;
    }

    async createStepPlan(planStepInfo) {
      const planStep = await this.http
        .post(`${environment.api}/api/v1/plan_step`, planStepInfo)
        .toPromise();
    }

    async updatePlanStep(planStepInfo) {
      const updatedPlanStep = await this.http
        .put(
          `${environment.api}/api/v1/plan_step/${planStepInfo.productId}`,
          planStepInfo
        )
        .toPromise();
    }

    async deletePlanStep(productId) {
      const deletedPlanStep = await this.http
        .delete(`${environment.api}/api/v1/plan_step/${productId}`, productId)
        .toPromise();
    }

    async getAllPlansByFarm(farmId) {
      let allPlans: any = await this.http
        .get(`${environment.api}/api/v1/plan`)
        .toPromise();
      await this.cacheService.insert('management-plans', allPlans.plans);
      allPlans = await this.cacheService.get('management-plans');
      return allPlans.filter((farmPlan) => farmPlan.farm_id == farmId);
    }

    async deletePlanById(planId) {
      return await this.http
        .delete(`${environment.api}/api/v1/plan/${planId}`)
        .toPromise();
    }

    async getPlanByOfflineId(planId) {
      const allPlans: any = await this.cacheService.get('management-plans');
      const filteredPlan: any = allPlans.find((plan) => plan.id == planId);
      return filteredPlan;
    }

    async getAllPlansByFarmOffline(farmId) {
      const allPlans: any = await this.cacheService.get('management-plans');
      const filteredPlans: any = allPlans.filter((plan) => plan.farm_id == farmId);
      return filteredPlans;
    }

    async deleteUser(userId) {
      return await this.http
        .delete(`${environment.api}/api/v1/user/${userId}`)
        .toPromise();
    }

    async getLaboratoryById(id: number): Promise<LaboratoryModel> {
      const laboratory = this.http
        .get<LaboratoryModel>(`${environment.api}/api/v1/laboratory/${id}`)
        .toPromise();

      return laboratory;
    }

    /**
     *
     * @returns Localiza os problemsCases cadastrados na base de dados
     */
    getProblemsCases(): Observable<ProblemsCasesModel[]> {
      return from(this.networkService.isConnected()).pipe(
        switchMap(isConnected => {
          if (isConnected) {
            return this.http
              .get<ProblemsCasesModel[]>(`${environment.api}/api/v1/problems_cases?orderby=problems_name&order=asc`)
              .pipe(
                tap(probcases => {
                  this.problemscases = probcases;
                  this.problemscasesSubject.next(probcases);
                  this.cacheService.insert('problems-cases', probcases);
                })
              );
          } else {
            return from(this.cacheService.get<ProblemsCasesModel[]>('problems-cases')).pipe(
              switchMap(cachedCases => of(cachedCases || []))
            );
          }
        })
      );
    }

    /**
     * 🔍 Obtém todos os tipos de problemas da API ou cache local se offline
     */
    getAllProblemsTypes(): Observable<ProblemsTypeModel[]> {
      return from(this.networkService.isConnected()).pipe(
        switchMap(isConnected => {
          if (isConnected) {
            return this.http
              .get<ProblemsTypeModel[]>(`${environment.api}/api/v1/problem_types?orderby=name&order=asc`)
              .pipe(
                tap(problemsTypes => {
                  this.problemsTypes = problemsTypes;
                  this.problemsTypesSubject.next(problemsTypes);
                  this.cacheService.insert('problem-types', problemsTypes);
                })
              );
          } else {
            return from(this.cacheService.get<ProblemsTypeModel[]>('problem-types')).pipe(
              switchMap(cachedTypes => of(cachedTypes || []))
            );
          }
        })
      );
    }



    async getAllVisitsForPeriod(farmId: number, startDate: string, endDate: string) {
      return await this.http.get<VisitDiaryModel[]>(
          `${environment.api}/api/visits?farmId=${farmId}&startDate=${startDate}&endDate=${endDate}`
      ).toPromise();
    }

    async saveReasonsToCache(reasons: any[]): Promise<void> {
      await this.cacheService.insert('reason-list', reasons);
    }

    async getReasonsFromCache(): Promise<any[]> {
      return await this.cacheService.get('reason-list');
    }
  }
